import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle, XCircle, Loader2, ShieldCheck, Calendar, FileText, Euro, ThumbsDown, MessageSquare } from 'lucide-react';

const API_BASE = '/api/public';

interface Installment {
  number: number;
  amount: number;
  due_date: string;
  description?: string;
}

interface ContractInfo {
  contract_number: string;
  installment_plan: Installment[] | null;
  total_amount: number | null;
  start_date: string | null;
  end_date: string | null;
  duration_months: number | null;
}

export default function PaymentSetup() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [tenantName, setTenantName] = useState('');
  const [contract, setContract] = useState<ContractInfo | null>(null);
  const [error, setError] = useState('');
  const [redirecting, setRedirecting] = useState(false);
  const [showRefuseForm, setShowRefuseForm] = useState(false);
  const [refuseReason, setRefuseReason] = useState('');
  const [refusing, setRefusing] = useState(false);
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    if (token) void loadPaymentSetup();
  }, [token]);

  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'refuse' && !loading && !error) {
      setShowRefuseForm(true);
    }
  }, [searchParams, loading, error]);

  async function loadPaymentSetup() {
    try {
      const res = await fetch(`${API_BASE}/payment-setup/${token}`);
      const data = await res.json();
      if (data.success) {
        setTenantName(data.data.tenant_name);
        if (data.data.contract) {
          const c = data.data.contract;
          setContract({
            ...c,
            installment_plan: typeof c.installment_plan === 'string'
              ? JSON.parse(c.installment_plan)
              : c.installment_plan
          });
        }
      } else {
        setError(data.error || 'Link non valido o scaduto');
      }
    } catch {
      setError('Impossibile caricare la pagina');
    } finally {
      setLoading(false);
    }
  }

  async function startSetup() {
    setRedirecting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/payment-setup/${token}/create-session`, { method: 'POST' });
      const data = await res.json();
      if (data.success && data.data.url) {
        window.location.href = data.data.url;
      } else {
        setError(data.error || 'Errore nella creazione della sessione di pagamento. Riprova più tardi.');
        setRedirecting(false);
      }
    } catch {
      setError('Errore di connessione. Riprova più tardi.');
      setRedirecting(false);
    }
  }

  async function handleRefuse() {
    setRefusing(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/payment-setup/${token}/refuse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: refuseReason || undefined }),
      });
      const data = await res.json();
      if (data.success) {
        setRefused(true);
        setShowRefuseForm(false);
      } else {
        setError(data.error || 'Errore nel rifiuto del piano.');
      }
    } catch {
      setError('Errore di connessione. Riprova più tardi.');
    } finally {
      setRefusing(false);
    }
  }

  function formatDate(d: string) {
    try { return new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return d; }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error && !tenantName) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Link Non Valido</h1>
          <p className="text-muted-foreground">{error}</p>
        </div>
      </div>
    );
  }

  if (refused) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <MessageSquare className="w-16 h-16 text-amber-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Piano Rateale Rifiutato</h1>
          <p className="text-muted-foreground mb-4">
            La Sua richiesta di modifica del piano rateale è stata registrata.
            Verrà contattato a breve per discutere un nuovo piano che soddisfi le Sue esigenze.
          </p>
          {refuseReason && (
            <div className="bg-muted/50 border border-border rounded-lg p-3 mt-4">
              <p className="text-muted-foreground text-xs mb-1">Il Suo messaggio:</p>
              <p className="text-foreground text-sm">{refuseReason}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  const installments = contract?.installment_plan || [];
  const totalAmount = installments.reduce((sum, i) => sum + parseFloat(String(i.amount)), 0);

  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-lg w-full">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-xl font-bold text-foreground mb-2">Configura Metodo di Pagamento</h1>
          <p className="text-muted-foreground">
            Gentile <strong>{tenantName}</strong>, configura il tuo metodo di pagamento per confermare il piano rateale.
          </p>
        </div>

        {contract && (
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-foreground">{contract.contract_number}</span>
            </div>

            {(contract.start_date || contract.duration_months) && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <Calendar className="w-4 h-4 text-muted-foreground/70" />
                <span>
                  {contract.start_date ? `Dal ${formatDate(contract.start_date)}` : ''}
                  {contract.end_date ? ` al ${formatDate(contract.end_date)}` : ''}
                  {contract.duration_months ? ` (${contract.duration_months} mesi)` : ''}
                </span>
              </div>
            )}

            {installments.length > 0 && (
              <div className="bg-muted/50 border border-border rounded-lg overflow-hidden mt-3">
                <div className="bg-blue-600 text-white px-4 py-2 text-sm font-semibold flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Piano Rateale
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted">
                      <th className="text-left px-4 py-2 text-muted-foreground font-medium">Rata</th>
                      <th className="text-left px-4 py-2 text-muted-foreground font-medium">Scadenza</th>
                      <th className="text-right px-4 py-2 text-muted-foreground font-medium">Importo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {installments.map((inst, idx) => (
                      <tr key={idx} className={`border-b border-gray-100 ${idx === 0 ? 'bg-blue-50' : ''}`}>
                        <td className="px-4 py-2.5 text-foreground font-medium">
                          {`${inst.number}ª rata`}
                          {inst.description ? <span className="text-muted-foreground text-xs ml-1">({inst.description})</span> : ''}
                        </td>
                        <td className="px-4 py-2.5 text-foreground">{formatDate(inst.due_date)}</td>
                        <td className="px-4 py-2.5 text-foreground text-right font-semibold">€ {parseFloat(String(inst.amount)).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted">
                      <td colSpan={2} className="px-4 py-2.5 text-foreground font-semibold">Totale</td>
                      <td className="px-4 py-2.5 text-blue-700 text-right font-bold">€ {totalAmount.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
          <p className="text-green-800 text-sm leading-relaxed">
            <strong>Nessun addebito verrà effettuato in questo momento.</strong> La configurazione serve solo per confermare il metodo di pagamento.
            Il primo addebito avverrà solo dopo la firma del contratto.
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="text-blue-800 font-medium text-sm mb-2">Metodi accettati:</h3>
          <ul className="text-blue-700 text-sm space-y-1">
            <li className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" /> Carta di credito / debito
            </li>
            <li className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" /> Addebito diretto SEPA (IBAN)
            </li>
          </ul>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <button
          onClick={startSetup}
          disabled={redirecting}
          className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium transition-colors"
        >
          {redirecting ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Reindirizzamento a Stripe...
            </>
          ) : (
            <>
              <CreditCard className="w-5 h-5" />
              Accetta e Configura Pagamento
            </>
          )}
        </button>

        {showRefuseForm ? (
          <div className="mt-4 border border-amber-200 bg-amber-50 rounded-lg p-4">
            <h3 className="text-amber-800 font-medium text-sm mb-2">Rifiuta Piano Rateale</h3>
            <p className="text-amber-700 text-xs mb-3">Inserisci un motivo per il rifiuto (opzionale). Verrai contattato per discutere un piano alternativo.</p>
            <textarea
              value={refuseReason}
              onChange={(e) => setRefuseReason(e.target.value)}
              placeholder="Es: Preferirei rate mensili più basse su un periodo più lungo..."
              className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm text-foreground focus:ring-2 focus:ring-inset focus:ring-amber-400 focus:border-amber-400 mb-3"
              rows={3}
            />
            <div className="flex gap-2">
              <button
                onClick={() => setShowRefuseForm(false)}
                className="flex-1 px-4 py-2 text-muted-foreground hover:text-foreground text-sm rounded-lg border border-border"
              >
                Annulla
              </button>
              <button
                onClick={handleRefuse}
                disabled={refusing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 disabled:opacity-50 text-sm font-medium"
              >
                {refusing ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsDown className="w-4 h-4" />}
                {refusing ? 'Invio...' : 'Conferma Rifiuto'}
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowRefuseForm(true)}
            className="w-full mt-3 flex items-center justify-center gap-2 px-6 py-2.5 text-muted-foreground hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors text-sm"
          >
            <ThumbsDown className="w-4 h-4" />
            Il piano non mi soddisfa
          </button>
        )}

        <div className="mt-4 flex items-center justify-center gap-2 text-muted-foreground text-xs">
          <ShieldCheck className="w-4 h-4" />
          <span>Pagamento sicuro gestito da Stripe</span>
        </div>
      </div>
    </div>
  );
}

export function PaymentSetupSuccess() {
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(true);
  const [verified, setVerified] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState<{ type?: string; last4?: string } | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      void verifySession(sessionId);
    } else {
      setVerifying(false);
      setVerified(true);
    }
  }, [searchParams]);

  async function verifySession(sessionId: string) {
    try {
      const res = await fetch(`${API_BASE}/payment-setup/verify-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sessionId }),
      });
      const data = await res.json();
      if (data.success) {
        setVerified(true);
        if (data.data?.payment_method) {
          setPaymentInfo(data.data.payment_method);
        }
      } else {
        setError(data.error || 'Errore nella verifica del pagamento');
      }
    } catch {
      setError('Errore di connessione. Il pagamento potrebbe essere stato comunque salvato.');
      setVerified(true);
    } finally {
      setVerifying(false);
    }
  }

  if (verifying) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Verifica in corso...</h1>
          <p className="text-muted-foreground">Stiamo confermando il tuo metodo di pagamento con Stripe.</p>
        </div>
      </div>
    );
  }

  if (error && !verified) {
    return (
      <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-foreground mb-2">Errore di Verifica</h1>
          <p className="text-muted-foreground mb-4">{error}</p>
        </div>
      </div>
    );
  }

  const pmLabel = paymentInfo?.type === 'card' ? 'Carta' : paymentInfo?.type === 'sepa_debit' ? 'SEPA' : null;

  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-foreground mb-2">Pagamento Configurato</h1>
        <p className="text-muted-foreground mb-4">
          Il metodo di pagamento è stato salvato con successo.
          {pmLabel && paymentInfo?.last4 && (
            <span className="block mt-2 font-medium text-foreground">
              {pmLabel} ****{paymentInfo.last4}
            </span>
          )}
        </p>
        <p className="text-muted-foreground mb-4">
          Riceverà a breve il contratto da firmare via email.
          Il primo addebito avverrà solo dopo la firma del contratto.
        </p>
        <div className="mt-4 flex items-center justify-center gap-2 text-green-600">
          <ShieldCheck className="w-5 h-5" />
          <span className="text-sm">Pagamento sicuro tramite Stripe</span>
        </div>
      </div>
    </div>
  );
}

export function PaymentSetupCancel() {
  return (
    <div className="min-h-screen bg-muted/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
        <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-foreground mb-2">Configurazione Annullata</h1>
        <p className="text-muted-foreground mb-4">
          La configurazione del metodo di pagamento è stata annullata. Puoi riprovare tramite il link ricevuto via email.
        </p>
      </div>
    </div>
  );
}
